import Vue from 'vue';
import { Module, Commit } from 'vuex';
import { RootState, Optional } from 'frontend/types';
import { Banner } from 'frontend/types/banners';
import useBanners from 'frontend/composables/useBanners';

interface BannerCache {
  [key: string]: Optional<Banner>;
}

interface BannerState {
  start_trial_cta_clicked_at: number;
  height: number;
  banner?: Banner;
  cache: BannerCache;
}

const banner: Module<BannerState, RootState> = {
  namespaced: true,

  state: {
    start_trial_cta_clicked_at: 0,
    height: 0,
    banner: undefined,
    cache: {},
  } as BannerState,

  actions: {
    startTrial({ commit }) {
      commit('startTrialCtaClicked');
    },
    async prefetchBanner({ commit, state }, { routePath }) {
      if (routePath in state.cache) {
        return;
      }
      const bannerData = await useBanners(routePath);
      commit('updateBanner', { routePath, bannerData });
    },
    hideBanner({ commit, rootState }: { commit: Commit; rootState: any }) {
      commit('updateBanner', {
        routePath: rootState.route.path,
        bannerData: undefined,
      });
    },
    invalidateBannerCache({ commit, state }, { routePath }) {
      const bannerData = state.cache[routePath];
      if (bannerData && bannerData.countdown_remaining_seconds) {
        // If the banner has a countdown, we don't want to cache it
        commit('removeStaleBanner', { routePath });
      }
    },
  },

  getters: {
    startTrialCtaClickedAt(state): number {
      return state.start_trial_cta_clicked_at;
    },
    bannerHeight(state): number {
      return state.height;
    },
    currentBanner(state, _, rootState: any): Optional<Banner> {
      return state.cache[rootState.route.path];
    },
  },

  mutations: {
    startTrialCtaClicked(state: BannerState) {
      state.start_trial_cta_clicked_at = Date.now();
    },
    bannerHeight(state: BannerState, height: number) {
      state.height = height;
    },
    updateBanner(state: BannerState, { routePath, bannerData }) {
      state.cache = { ...state.cache, [routePath]: bannerData };
    },
    removeStaleBanner(state: BannerState, { routePath }) {
      Vue.delete(state.cache, routePath);
    },
  },
};

export default banner;
